import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Followup = { id: string; followup_on: string; note_text: string; status: string };

type CaseRow = {
  id: string;
  case_no: string;
  referral_on: string;
  referral_reason: string;
  source_type: string;
  source_id?: string | null;
  plan_summary?: string | null;
  plan_goals?: string | null;
  plan_started_on?: string | null;
  outcome_code?: string | null;
  outcome_summary?: string | null;
  status: string;
  record_version: number;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
  section_name?: string;
  followups: Followup[];
};

type PlanValues = {
  plan_summary: string;
  plan_goals?: string;
  plan_started_on: dayjs.Dayjs;
};

export default function InterventionDetailPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [planForm] = Form.useForm<PlanValues>();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.intervention.manage') || user?.permissions?.includes('education.admin')
  );

  const query = useQuery({
    queryKey: ['education-intervention-case', id],
    enabled: Boolean(id),
    queryFn: async () => (await api.get<CaseRow>(`/education/intervention/cases/${id}`)).data,
  });
  const row = query.data;
  const canPlan = row && ['referred', 'planning', 'active'].includes(row.status);
  const canFollow = row && row.status === 'active';
  const canOutcome = row && row.status === 'active' && Boolean(row.plan_summary);
  const canCancel = row && ['referred', 'planning', 'active'].includes(row.status);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['education-intervention-case', id] });
    queryClient.invalidateQueries({ queryKey: ['education-intervention'] });
  };

  const savePlan = useMutation({
    mutationFn: (payload: PlanValues & { activate?: boolean }) =>
      api.post(`/education/intervention/cases/${id}/plan`, {
        plan_summary: payload.plan_summary,
        plan_goals: payload.plan_goals,
        plan_started_on: payload.plan_started_on.format('YYYY-MM-DD'),
        activate: payload.activate,
        record_version: row?.record_version,
      }),
    onSuccess: () => {
      message.success(t('interventionPlanSaved'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const addFollowup = useMutation({
    mutationFn: (values: { followup_on: dayjs.Dayjs; note_text: string }) =>
      api.post(`/education/intervention/cases/${id}/followups`, {
        followup_on: values.followup_on.format('YYYY-MM-DD'),
        note_text: values.note_text,
      }),
    onSuccess: () => {
      message.success(t('interventionFollowupAdded'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const followStatus = useMutation({
    mutationFn: ({ followupId, status }: { followupId: string; status: string }) =>
      api.post(`/education/intervention/followups/${followupId}/status`, { status }),
    onSuccess: () => {
      message.success(t('interventionFollowupUpdated'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const submitOutcome = useMutation({
    mutationFn: (values: { outcome_code: string; outcome_summary: string }) =>
      api.post(`/education/intervention/cases/${id}/outcome`, {
        ...values,
        record_version: row?.record_version,
      }),
    onSuccess: () => {
      message.success(t('interventionOutcomeSubmitted'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const cancel = useMutation({
    mutationFn: () =>
      api.post(`/education/intervention/cases/${id}/cancel`, { record_version: row?.record_version }),
    onSuccess: () => {
      message.success(t('interventionCancelled'));
      invalidate();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={row?.case_no || t('intervention')}
      description={t('interventionIntro')}
      tags={row ? <Tag>{row.status}</Tag> : null}
      extra={<Button onClick={() => navigate('/education/intervention')}>{t('intervention')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('interventionHumanApprovedHint')} />
        <Card size="small" loading={query.isLoading}>
          {row ? (
            <Descriptions column={{ xs: 1, md: 2 }} size="small">
              <Descriptions.Item label={t('student')}>
                {`${row.admission_no || ''} — ${row.first_name || ''} ${row.last_name || ''}`.trim()}
              </Descriptions.Item>
              <Descriptions.Item label={t('section')}>{row.section_name || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('referralOn')}>{row.referral_on}</Descriptions.Item>
              <Descriptions.Item label={t('interventionSource')}>{row.source_type}</Descriptions.Item>
              <Descriptions.Item label={t('referralReason')} span={2}>
                {row.referral_reason}
              </Descriptions.Item>
              {row.plan_summary ? (
                <>
                  <Descriptions.Item label={t('planStartedOn')}>{row.plan_started_on || '—'}</Descriptions.Item>
                  <Descriptions.Item label={t('planSummary')} span={2}>
                    {row.plan_summary}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('planGoals')} span={2}>
                    {row.plan_goals || '—'}
                  </Descriptions.Item>
                </>
              ) : null}
              {row.outcome_summary ? (
                <>
                  <Descriptions.Item label={t('outcomeCode')}>{row.outcome_code || '—'}</Descriptions.Item>
                  <Descriptions.Item label={t('outcomeSummary')} span={2}>
                    {row.outcome_summary}
                  </Descriptions.Item>
                </>
              ) : null}
            </Descriptions>
          ) : null}
        </Card>

        {canManage && canPlan ? (
          <Card size="small" title={t('interventionPlan')}>
            <Form
              form={planForm}
              layout="vertical"
              initialValues={{
                plan_summary: row?.plan_summary || undefined,
                plan_goals: row?.plan_goals || undefined,
                plan_started_on: row?.plan_started_on ? dayjs(row.plan_started_on) : dayjs(),
              }}
              onFinish={(values) => savePlan.mutate({ ...values, activate: false })}
            >
              <Form.Item name="plan_summary" label={t('planSummary')} rules={[{ required: true }]}>
                <Input.TextArea rows={2} maxLength={1000} />
              </Form.Item>
              <Form.Item name="plan_goals" label={t('planGoals')}>
                <Input.TextArea rows={3} maxLength={2000} />
              </Form.Item>
              <Form.Item name="plan_started_on" label={t('planStartedOn')} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
              </Form.Item>
              <Space>
                <Button htmlType="submit" loading={savePlan.isPending}>
                  {t('savePlan')}
                </Button>
                <Button
                  type="primary"
                  loading={savePlan.isPending}
                  onClick={async () => {
                    try {
                      const values = await planForm.validateFields();
                      savePlan.mutate({ ...values, activate: true });
                    } catch {
                      /* validation shown by form */
                    }
                  }}
                >
                  {t('activatePlan')}
                </Button>
              </Space>
            </Form>
          </Card>
        ) : null}

        {canManage && canFollow ? (
          <Card size="small" title={t('interventionFollowups')}>
            <Form
              layout="vertical"
              initialValues={{ followup_on: dayjs() }}
              onFinish={(values) => addFollowup.mutate(values)}
              style={{ marginBottom: 16, maxWidth: 560 }}
            >
              <Form.Item name="followup_on" label={t('followupOn')} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
              </Form.Item>
              <Form.Item name="note_text" label={t('note')} rules={[{ required: true }]}>
                <Input.TextArea rows={2} maxLength={1000} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={addFollowup.isPending}>
                {t('addFollowup')}
              </Button>
            </Form>
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={row?.followups || []}
              columns={[
                { title: t('followupOn'), dataIndex: 'followup_on', width: 120 },
                { title: t('note'), dataIndex: 'note_text' },
                {
                  title: t('status'),
                  dataIndex: 'status',
                  width: 100,
                  render: (v: string) => <Tag>{v}</Tag>,
                },
                {
                  title: t('actions'),
                  width: 180,
                  render: (_: unknown, fu: Followup) =>
                    fu.status === 'open' ? (
                      <Space>
                        <Button size="small" onClick={() => followStatus.mutate({ followupId: fu.id, status: 'done' })}>
                          {t('done')}
                        </Button>
                        <Button
                          size="small"
                          onClick={() => followStatus.mutate({ followupId: fu.id, status: 'cancelled' })}
                        >
                          {t('statusCancelled')}
                        </Button>
                      </Space>
                    ) : null,
                },
              ]}
            />
          </Card>
        ) : row?.followups?.length ? (
          <Card size="small" title={t('interventionFollowups')}>
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={row.followups}
              columns={[
                { title: t('followupOn'), dataIndex: 'followup_on', width: 120 },
                { title: t('note'), dataIndex: 'note_text' },
                {
                  title: t('status'),
                  dataIndex: 'status',
                  width: 100,
                  render: (v: string) => <Tag>{v}</Tag>,
                },
              ]}
            />
          </Card>
        ) : null}

        {canManage && canOutcome ? (
          <Card size="small" title={t('submitOutcome')}>
            <Form layout="vertical" style={{ maxWidth: 560 }} onFinish={(values) => submitOutcome.mutate(values)}>
              <Form.Item name="outcome_code" label={t('outcomeCode')} rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: 'improved', label: t('outcomeImproved') },
                    { value: 'partial', label: t('outcomePartial') },
                    { value: 'no_change', label: t('outcomeNoChange') },
                    { value: 'escalated', label: t('outcomeEscalated') },
                    { value: 'withdrawn', label: t('outcomeWithdrawn') },
                  ]}
                />
              </Form.Item>
              <Form.Item name="outcome_summary" label={t('outcomeSummary')} rules={[{ required: true }]}>
                <Input.TextArea rows={3} maxLength={1000} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={submitOutcome.isPending}>
                {t('submitForApproval')}
              </Button>
            </Form>
          </Card>
        ) : null}

        {canManage && canCancel ? (
          <Button danger loading={cancel.isPending} onClick={() => cancel.mutate()}>
            {t('cancelCase')}
          </Button>
        ) : null}
      </Space>
    </EducationPageChrome>
  );
}
